import api from './api';

// ─── In-Memory Cache (Tab-lokal, mit TTL) ───
// Korrespondenz-Vorlagen ändern sich selten, werden aber oft (Composer, Briefe,
// Kampagnen) abgefragt. Cache reduziert wahrnehmbare Latenz beim Öffnen des
// Vorlagen-Modals von ~1-3s auf ~0ms.
const TPL_CACHE_TTL_MS = 5 * 60 * 1000; // 5 Minuten
const _cache = new Map(); // key → { ts, data }

const _cacheKey = (kind, params = {}) => {
  const sorted = Object.keys(params).sort().map((k) => `${k}=${params[k] ?? ''}`).join('&');
  return `${kind}?${sorted}`;
};
const _cacheGet = (key) => {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > TPL_CACHE_TTL_MS) {
    _cache.delete(key);
    return null;
  }
  return entry.data;
};
const _cacheSet = (key, data) => _cache.set(key, { ts: Date.now(), data });

/** Cache komplett oder selektiv invalidieren. Wird nach Mutations aufgerufen. */
const _cacheInvalidate = (prefixes = []) => {
  if (!prefixes.length) { _cache.clear(); return; }
  for (const k of Array.from(_cache.keys())) {
    if (prefixes.some((p) => k.startsWith(p))) _cache.delete(k);
  }
};

const templateService = {
  // ==================== KATEGORIEN ====================
  getCategories: async (typ = null) => {
    const params = typ ? { typ } : {};
    const key = _cacheKey('categories', params);
    const cached = _cacheGet(key);
    if (cached) return cached;
    const response = await api.get('/correspondence-templates/categories', { params });
    _cacheSet(key, response.data);
    return response.data;
  },

  createCategory: async (data) => {
    const response = await api.post('/correspondence-templates/categories', data);
    _cacheInvalidate(['categories']);
    return response.data;
  },

  updateCategory: async (categoryId, data) => {
    const response = await api.put(`/correspondence-templates/categories/${categoryId}`, data);
    _cacheInvalidate(['categories']);
    return response.data;
  },

  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/correspondence-templates/categories/${categoryId}`);
    _cacheInvalidate(['categories', 'email', 'letter']);
    return response.data;
  },

  // ==================== E-MAIL VORLAGEN ====================
  getEmailTemplates: async (categoryId = null, isActive = null) => {
    const params = {};
    if (categoryId) params.category_id = categoryId;
    if (isActive !== null) params.is_active = isActive;
    const key = _cacheKey('email', params);
    const cached = _cacheGet(key);
    if (cached) return cached;
    const response = await api.get('/correspondence-templates/email', { params });
    _cacheSet(key, response.data);
    return response.data;
  },

  getEmailTemplate: async (templateId) => {
    const response = await api.get(`/correspondence-templates/email/${templateId}`);
    return response.data;
  },

  createEmailTemplate: async (data) => {
    const response = await api.post('/correspondence-templates/email', data);
    _cacheInvalidate(['email']);
    return response.data;
  },

  updateEmailTemplate: async (templateId, data) => {
    const response = await api.put(`/correspondence-templates/email/${templateId}`, data);
    _cacheInvalidate(['email']);
    return response.data;
  },

  // Favoritenstern (je Benutzer) setzen/entfernen → { id, is_favorite }
  toggleEmailTemplateFavorit: async (templateId) => {
    const response = await api.post(`/correspondence-templates/email/${templateId}/favorit`);
    _cacheInvalidate(['email']);
    return response.data;
  },

  aiGenerateEmailContent: async (purpose) => {
    const response = await api.post('/correspondence-templates/email/ai-generate', { purpose });
    return response.data;
  },

  deleteEmailTemplate: async (templateId) => {
    const response = await api.delete(`/correspondence-templates/email/${templateId}`);
    _cacheInvalidate(['email']);
    return response.data;
  },

  reorderEmailTemplates: async (categoryId, templateIds) => {
    const response = await api.post('/correspondence-templates/email/reorder', {
      category_id: categoryId,
      template_ids: templateIds,
    });
    _cacheInvalidate(['email']);
    return response.data;
  },

  duplicateEmailTemplate: async (templateId) => {
    const response = await api.post(`/correspondence-templates/email/${templateId}/duplicate`);
    _cacheInvalidate(['email']);
    return response.data;
  },

  // ==================== BRIEF VORLAGEN ====================
  getLetterTemplates: async (categoryId = null, isActive = null) => {
    const params = {};
    if (categoryId) params.category_id = categoryId;
    if (isActive !== null) params.is_active = isActive;
    const key = _cacheKey('letter', params);
    const cached = _cacheGet(key);
    if (cached) return cached;
    const response = await api.get('/correspondence-templates/letter', { params });
    _cacheSet(key, response.data);
    return response.data;
  },

  getLetterTemplate: async (templateId) => {
    const response = await api.get(`/correspondence-templates/letter/${templateId}`);
    return response.data;
  },

  createLetterTemplate: async (data) => {
    const response = await api.post('/correspondence-templates/letter', data);
    _cacheInvalidate(['letter']);
    return response.data;
  },

  updateLetterTemplate: async (templateId, data) => {
    const response = await api.put(`/correspondence-templates/letter/${templateId}`, data);
    _cacheInvalidate(['letter']);
    return response.data;
  },

  deleteLetterTemplate: async (templateId) => {
    const response = await api.delete(`/correspondence-templates/letter/${templateId}`);
    _cacheInvalidate(['letter']);
    return response.data;
  },

  reorderLetterTemplates: async (templateIds) => {
    const response = await api.post('/correspondence-templates/letter/reorder', {
      template_ids: templateIds,
    });
    _cacheInvalidate(['letter']);
    return response.data;
  },

  duplicateLetterTemplate: async (templateId) => {
    const response = await api.post(`/correspondence-templates/letter/${templateId}/duplicate`);
    return response.data;
  },

  // ==================== BRIEF-KATEGORIEN ====================
  getLetterCategories: async () => {
    const response = await api.get('/correspondence-templates/categories', { params: { typ: 'letter' } });
    return response.data;
  },

  createLetterCategory: async (data) => {
    const response = await api.post('/correspondence-templates/categories', {
      ...data,
      typ: 'letter',
      sort_order: 100
    });
    return response.data;
  },

  // ==================== BRIEFKOPF ====================
  getLetterhead: async () => {
    const response = await api.get('/correspondence-templates/letterhead');
    return response.data;
  },

  updateLetterhead: async (data) => {
    const response = await api.put('/correspondence-templates/letterhead', data);
    return response.data;
  },

  // ==================== SIGNATUREN ====================
  getSignatures: async () => {
    const response = await api.get('/correspondence-templates/signatures');
    return response.data;
  },

  getSignature: async (signatureId) => {
    const response = await api.get(`/correspondence-templates/signatures/${signatureId}`);
    return response.data;
  },

  createSignature: async (data) => {
    const response = await api.post('/correspondence-templates/signatures', data);
    return response.data;
  },

  updateSignature: async (signatureId, data) => {
    const response = await api.put(`/correspondence-templates/signatures/${signatureId}`, data);
    return response.data;
  },

  deleteSignature: async (signatureId) => {
    const response = await api.delete(`/correspondence-templates/signatures/${signatureId}`);
    return response.data;
  },

  // ==================== TEXTBAUSTEINE ====================
  getTextBlocks: async (isActive = null) => {
    const params = {};
    if (isActive !== null) params.is_active = isActive;
    const response = await api.get('/correspondence-templates/text-blocks', { params });
    return response.data;
  },

  createTextBlock: async (data) => {
    const response = await api.post('/correspondence-templates/text-blocks', data);
    return response.data;
  },

  updateTextBlock: async (blockId, data) => {
    const response = await api.put(`/correspondence-templates/text-blocks/${blockId}`, data);
    return response.data;
  },

  deleteTextBlock: async (blockId) => {
    const response = await api.delete(`/correspondence-templates/text-blocks/${blockId}`);
    return response.data;
  },

  // Löst {{platzhalter}} in beliebigem HTML auf (Kontakt via address_id)
  renderSnippet: async ({ html, address_id = null, order_id = null, project_id = null }) => {
    const response = await api.post('/correspondence-templates/render/snippet', {
      html, address_id, order_id, project_id,
    });
    return response.data;
  },

  // ==================== PLATZHALTER ====================
  getPlaceholders: async () => {
    const response = await api.get('/correspondence-templates/placeholders');
    return response.data;
  },

  // ==================== RENDER & SEND ====================
  renderEmail: async (data) => {
    const response = await api.post('/correspondence-templates/render/email', data);
    return response.data;
  },

  renderLetter: async (data) => {
    const response = await api.post('/correspondence-templates/render/letter', data);
    return response.data;
  },

  generatePDF: async (data) => {
    const response = await api.post('/correspondence-templates/generate/pdf', data);
    return response.data;
  },

  // ==================== CORRESPONDENCE LOG ====================
  getCorrespondenceLog: async (addressId) => {
    const response = await api.get(`/correspondence-templates/log/${addressId}`);
    return response.data;
  },

  // Serienmail: je Empfänger-Adresse ein Eintrag (Protokoll, Historie, Vertriebs-Journal)
  createCorrespondenceLogMehrfach: async (data) => {
    const response = await api.post('/correspondence-templates/log/mehrfach', data);
    return response.data;
  },

  deleteCorrespondenceLog: async (logId) => {
    const response = await api.delete(`/correspondence-templates/log/${logId}`);
    return response.data;
  },

  createCorrespondenceLog: async (data) => {
    const response = await api.post('/correspondence-templates/log', data);
    return response.data;
  },

  // ==================== LEGACY (bestehende Funktionen) ====================
  // Templates CRUD
  getTemplates: async (templateType = null, includeGlobal = true) => {
    const params = { include_global: includeGlobal };
    if (templateType) params.template_type = templateType;
    const response = await api.get('/templates/', { params });
    return response.data;
  },

  getAllTemplates: async () => {
    const response = await api.get('/templates/all');
    return response.data;
  },

  getTemplate: async (templateId) => {
    const response = await api.get(`/templates/${templateId}`);
    return response.data;
  },

  createTemplate: async (templateData) => {
    const response = await api.post('/templates/', templateData);
    return response.data;
  },

  updateTemplate: async (templateId, templateData) => {
    const response = await api.put(`/templates/${templateId}`, templateData);
    return response.data;
  },

  deleteTemplate: async (templateId) => {
    const response = await api.delete(`/templates/${templateId}`);
    return response.data;
  },

  // Folders CRUD
  getFolders: async () => {
    const response = await api.get('/templates/folders/');
    return response.data;
  },

  createFolder: async (folderData) => {
    const response = await api.post('/templates/folders/', folderData);
    return response.data;
  },

  updateFolder: async (folderId, folderData) => {
    const response = await api.put(`/templates/folders/${folderId}`, folderData);
    return response.data;
  },

  deleteFolder: async (folderId) => {
    const response = await api.delete(`/templates/folders/${folderId}`);
    return response.data;
  },

  // Export
  exportToDocx: async (entityType, entityId, templateId = null, includeCustomFields = true) => {
    const response = await api.post('/templates/export/docx', {
      entity_type: entityType,
      entity_id: entityId,
      template_id: templateId,
      export_format: 'docx',
      include_custom_fields: includeCustomFields
    }, {
      responseType: 'blob'
    });
    return response.data;
  },

  downloadDocx: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  getExportPreview: async (entityType, entityId) => {
    const response = await api.get(`/templates/export/preview/${entityType}/${entityId}`);
    return response.data;
  },

  getTextBlockCategories: async () => {
    const response = await api.get('/templates/text-blocks/categories');
    return response.data;
  },

  // Hilfsfunktionen für Styling-Defaults
  getDefaultStyle: () => ({
    primary_color: '#1e40af',
    secondary_color: '#64748b',
    font_family: 'Arial',
    font_size: 11,
    header_size: 14,
    line_spacing: 1.15
  }),

  getDefaultHeader: () => ({
    logo_url: null,
    company_name: '',
    address_line1: '',
    address_line2: '',
    phone: '',
    email: '',
    website: '',
    show_logo: false,
    logo_position: 'left'
  }),

  getDefaultFooter: () => ({
    text_left: '',
    text_center: '',
    text_right: '',
    show_page_numbers: true,
    bank_details: '',
    tax_info: ''
  })
};

export default templateService;
